import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FormService } from '@pages/checkout-app/services';
import { Shippings } from '@pages/checkout-app/types';
import { Store } from '@ngrx/store';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDialog } from '@pages/checkout-app/components/dialogs/delete-dialog/delete-dialog';
import { initLocationsOptions, CHECKOUT_URL } from '@pages/checkout-app/utils';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, switchMap, catchError, finalize } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { ShopService } from '@app/services/shops/shop.service';
import { Shop } from '@app/services/shops/shop.model';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { ToastService } from '@storybook/services/toast/toast.service';
import { RuleNameToDisplayNameMap } from '@pages/checkout-app/configs';
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { ThirdPartyEnabledComponent } from '@pages/checkout-app/components/dialogs/third-party-enabled/third-party-enabled.component';
import { HttpStatusCode } from '@pdqdev/pdq-shared/dist/utils/http-status-codes';
import { CheckoutListConfig } from '@pages/checkout-app/checkout-layout/main/components/delivery-rates/checkout-list/checkout-list.models';
import { AbtestDialogComponent } from '@pages/checkout-app/components/abtest/dialogs/abtest-dialog/abtest-dialog.component';
import { AbTestV2MigrationDialog } from '@app/pages/checkout-app/components/abtest/dialogs/abtest-v2-migration-dialog/abtest-v2-migration-dialog.component';

enum ACTIONS {
  DELETE = 'delete',
  EDIT = 'edit',
  DUPLICATE = 'duplicate',
  AB_TEST = 'abTest',
  VIEW_ABTEST = 'viewABTest',
}

@Component({
  selector: 'app-checkout-list',
  templateUrl: './checkout-list.html',
  styleUrls: ['./checkout-list.scss'],
  standalone: false,
})
export class CheckoutListComponent implements OnInit, OnDestroy {
  readonly CHECKOUT_SETTINGS_URL = '/settings/checkout';
  isCheckoutEcommerceEnabled$ = this.store.select(fromCheckoutReducer.checkoutEcommerceEnabled);
  isShoppingBannerClosed$ = this.store.select(fromCheckoutReducer.shoppingBannerClose);
  isABTestBannerClosed$ = this.store.select(fromCheckoutReducer.abTestBannerClose);
  abTestWarning$ = this.store.select(fromCheckoutReducer.abTestWarning);
  mainLoading$ = this.store.select(fromCheckoutReducer.selectMainLoading);
  loadCheckouts$ = this.store.select(fromCheckoutReducer.selectShippingMethods);
  loadFallbackMethod$ = this.store.select(fromCheckoutReducer.selectFallbackMethod);
  settings$ = this.store.select(fromCheckoutReducer.selectSettings);
  checkoutsList: Shippings[];
  editMode: boolean = false;
  formGroup: UntypedFormGroup;
  activeMode = new UntypedFormControl();
  activeEnable: boolean;
  shippingMethodsWithRunningAbTestsV1: string[] = [];
  migrationEnabled = false;

  columnsConfig = CheckoutListConfig;

  shop$: Observable<Shop>;
  shop: Shop;
  currency: string;

  actionOptions: DropdownOption[] = [
    {
      label: 'Edit',
      value: 'edit',
    },
    {
      label: 'View running A/B test',
      value: 'viewABTest',
    },
    {
      label: 'Performance & A/B test',
      value: 'abTest',
    },
    {
      label: 'Duplicate',
      value: 'duplicate',
    },
    {
      label: 'Delete',
      value: 'delete',
      variant: 'danger',
    },
  ];
  RulesDisplayNameMap: Map<string, string> = RuleNameToDisplayNameMap;
  disableEnableBtnMap: Record<number, boolean> = {};
  useNewCheckout: boolean = false;

  private destroyed$ = new Subject<boolean>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private formService: FormService,
    private router: Router,
    private shopService: ShopService,
    private dialog: MatDialog,
    private detectSuccessOrFailure$: Actions,
    private store: Store,
    private toastService: ToastService,
    private cs: CheckoutService,
    private cd: ChangeDetectorRef,
  ) {
    this.initIsCheckoutEcommerceEnabled();
    this.detectLoadSuccess();
    this.detectDeleteSuccess();
  }

  ngOnInit(): void {
    this.shop$ = this.shopService.shop$;
    this.shop$.pipe(takeUntil(this.destroyed$)).subscribe((shop) => {
      this.shop = shop;
      this.currency = this.shopService.shopCurrencySymbol;
      initLocationsOptions(this.shop);
      if (shop?.metadata?.useNewCheckout) {
        this.useNewCheckout = true;
        this.cd.detectChanges();
      } else {
        this.store.dispatch(CheckoutActions.loadCheckouts());
      }
    });
    this.loadCheckouts$.pipe(takeUntil(this.destroyed$)).subscribe((shippings: Shippings[]) => {
      this.shippingMethodsWithRunningAbTestsV1 = [];
      for (const shipping of shippings) {
        if (shipping.runningTest) {
          this.shippingMethodsWithRunningAbTestsV1.push(shipping.name);
        }
      }
    });
  }

  initIsCheckoutEcommerceEnabled() {
    this.isCheckoutEcommerceEnabled$
      .pipe(
        takeUntil(this.destroyed$),
        switchMap((enabled) => {
          if (enabled) {
            return of(enabled);
          }
          return this.cs.isCheckoutEnabledOnEcommerce();
        }),
        catchError((e) => {
          return of({ status: false, errorStatus: e.status });
        }),
      )
      .subscribe((response: any) => {
        if (typeof response === 'boolean' && !!response) {
          this.store.dispatch(CheckoutActions.checkoutEcommerceEnabled());
        } else if (response?.errorStatus === HttpStatusCode.UNPROCESSABLE_ENTITY && this.shop?.platform == 'shopify') {
          this.dialog.open(ThirdPartyEnabledComponent, { disableClose: true, autoFocus: false });
        }
      });
  }

  detectDeleteSuccess(): void {
    this.detectSuccessOrFailure$
      .pipe(
        takeUntil(this.destroyed$),
        ofType(CheckoutActions.deleteCheckoutSuccess || CheckoutActions.deleteCheckoutFailure),
      )
      .subscribe(() => {
        this.toastService.showToast({
          message: 'Delivery method has been deleted',
          mode: 'info',
          duration: 5000,
        });
      });
  }

  detectLoadSuccess(): void {
    this.detectSuccessOrFailure$
      .pipe(takeUntil(this.destroyed$), ofType(CheckoutActions.loadCheckoutsFailure))
      .subscribe((res) => {
        const stateMsg = res?.type?.split(']')[1];
        this.toastService.showToast({
          message: stateMsg,
          mode: 'danger',
          duration: 5000,
        });
      });
  }

  upgradeToV2AbTesting() {
    this.formService.loadVariantTestGroups;
    const dialogRef = this.dialog.open(AbTestV2MigrationDialog, {
      data: {
        title: 'Upgrade to AB test V2',
        body: 'Upgrade now to start leveraging the full power of AB testing.',
        runningAbTestNames: this.shippingMethodsWithRunningAbTestsV1,
      },
    });
    dialogRef.componentInstance.onConfirm.subscribe(() => {
      this.formService.enableNewAbTesting().subscribe(() => {
        this.shopService.refreshShop();
        this.toastService.showToast({
          message: 'AB Testing V2 has been enabled. Please wait for changes to take effect...',
          mode: 'info',
          duration: 5000,
        });
      });
    });
  }

  onLoadCheckout(checkoutId: string) {
    const queryParams: Params = { checkoutId };
    this.router.navigate([`${CHECKOUT_URL}/methods/edit`], {
      queryParams: queryParams,
    });
  }

  onAction(event: any, checkout) {
    if (!event) {
      return;
    }
    const { value } = event;
    switch (value) {
      case ACTIONS.EDIT:
        this.onLoadCheckout(checkout.id);
        break;
      case ACTIONS.AB_TEST:
        this.onABTest(checkout.id);
        break;
      case ACTIONS.DUPLICATE:
        this.onDuplicate(checkout.id);
        break;
      case ACTIONS.DELETE:
        this.onDelete(checkout.id, checkout.name, checkout.shippingMethod?.category);
        break;
      case ACTIONS.VIEW_ABTEST:
        this.onABTestPreview(checkout);
    }
  }

  onABTestPreview(checkout) {
    if (checkout.runningTest) {
      this.store.dispatch(CheckoutActions.setActiveShipping({ shippingId: checkout.id }));
      this.dialog.open(AbtestDialogComponent, {
        data: { mode: ACTIONS.EDIT, testVariant: checkout.runningTest, isPreviewOnly: true },
      });
    }
  }

  onShoppingBannerClose() {
    this.store.dispatch(CheckoutActions.closeShoppingBanner());
  }

  onABTestBannerClose() {
    this.store.dispatch(CheckoutActions.closeABTestBanner());
  }

  onABTest(checkoutId: string) {
    this.router.navigate([`${CHECKOUT_URL}/ab-testing`], { queryParams: { id: checkoutId } });
  }

  onDuplicate(checkoutId: string) {
    this.formService.onDuplicate.next(checkoutId);
    this.router.navigate([`${CHECKOUT_URL}/methods/new`]);
  }

  onDelete(checkoutId: string, checkoutName: string, checkoutMethod: string): void {
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: {
        checkoutName: checkoutName,
        checkoutId: checkoutId,
        checkoutMethod: checkoutMethod,
      },
    });
    const deleteSubscribe$ = dialogRef.componentInstance.onDelete.subscribe((checkoutId: string) => {
      this.store.dispatch(
        CheckoutActions.deleteCheckout({
          checkoutId: checkoutId,
        }),
      );
    });
    dialogRef.afterClosed().subscribe(() => {
      deleteSubscribe$.unsubscribe();
    });
  }

  newShipping() {
    this.router.navigate([`${CHECKOUT_URL}/methods/new`]);
  }

  onModeChange(event, checkoutId: string): void {
    let sendData: Shippings;
    this.disableEnableBtnMap[+checkoutId] = true;
    sendData = { enabled: event };
    this.formService
      .onCheckoutChangeActiveMode(sendData, checkoutId)
      .pipe(finalize(() => (this.disableEnableBtnMap[+checkoutId] = false)))
      .subscribe(
        (checkout) => {
          this.store.dispatch(CheckoutActions.changeCheckoutActiveModeSuccess({ checkout }));
        },
        (err) => {
          this.store.dispatch(CheckoutActions.onEnableCheckoutMethodFailure({ enabled: false, checkoutId }));
          if (this.shop?.platform == 'shopify') {
            this.dialog.open(ThirdPartyEnabledComponent, { disableClose: true, autoFocus: false });
          }
          this.toastService.showToast({
            message: err?.error ?? 'Failed to enable method',
            mode: 'danger',
            duration: 5000,
          });
        },
      );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  navigateToCheckoutSettings() {
    this.router.navigate([this.CHECKOUT_SETTINGS_URL]);
  }
}
